import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, Loader2, Square } from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import VoiceInputButton from './VoiceInputButton';
import FilePreviewChips from './FilePreviewChips';
import { useFileUpload } from '@/hooks/useFileUpload';

interface MessageInputProps {
  onSend: (message: string, uploadIds?: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  /**
   * When set alongside `isStreaming`, the send button is replaced with a
   * stop button that calls this handler. The textarea is locked while the
   * stream is running regardless. Decision 1a (between-turns only) means
   * the stop is best-effort — the current turn always runs to completion.
   */
  onStop?: () => void;
  isStreaming?: boolean;
  /**
   * While the stop request is in-flight, the button shows a spinner and
   * becomes unclickable (decision 6 — hard-block until the backend
   * confirms the loop has finalized).
   */
  isAborting?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Type a message...',
  onStop,
  isStreaming = false,
  isAborting = false,
}) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { pendingFiles, isUploading, addFiles, removeFile, uploadAll, clear } = useFileUpload();

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [message]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = message.trim();
    if ((!trimmed && pendingFiles.length === 0) || disabled || isUploading) return;

    try {
      let uploadIds: string[] | undefined;
      if (pendingFiles.length > 0) {
        uploadIds = await uploadAll();
      }

      onSend(trimmed || '(file attached)', uploadIds);
      setMessage('');
      clear();

      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const error = addFiles(e.target.files);
      if (error) toast.error(error);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Textarea locks during streaming so the user can't queue a message while
  // waiting for the agent to finish. During isAborting we also keep it
  // locked (decision 6 — hard-block until backend confirms the abort landed).
  const isBusy = disabled || isUploading || isStreaming || isAborting;
  const canSend = (message.trim().length > 0 || pendingFiles.length > 0) && !isBusy;
  const showStopButton = !!onStop && (isStreaming || isAborting);

  return (
    <form
      onSubmit={handleSubmit}
      className="border-t border-border bg-background"
    >
      <FilePreviewChips files={pendingFiles} onRemove={removeFile} disabled={isBusy} />

      <div className="flex items-end gap-2 px-4 py-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf,.pdf,.csv,.txt,.docx,.xlsx"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isBusy}
          className={clsx(
            'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
            'text-muted-foreground hover:bg-muted transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          aria-label="Attach image"
        >
          <Paperclip className="w-5 h-5" />
        </button>

        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isBusy}
            rows={1}
            className={clsx(
              'w-full resize-none rounded-xl border border-input',
              'bg-muted px-4 py-3',
              'text-sm text-foreground',
              'placeholder:text-muted-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
              'transition-colors',
              isBusy && 'opacity-50 cursor-not-allowed'
            )}
          />
        </div>
        <VoiceInputButton
          onTranscriptReady={(result) => {
            setMessage(result.transcript);
            requestAnimationFrame(() => textareaRef.current?.focus());
          }}
          disabled={isBusy}
        />
        {showStopButton ? (
          <button
            type="button"
            onClick={onStop}
            disabled={isAborting}
            aria-label={isAborting ? 'Stopping…' : 'Stop response'}
            className={clsx(
              'flex-shrink-0 w-10 h-10 rounded-full',
              'flex items-center justify-center',
              'bg-foreground text-background',
              'hover:opacity-90 transition-opacity',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              'disabled:opacity-60 disabled:cursor-not-allowed'
            )}
          >
            {isAborting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Square className="w-3.5 h-3.5" fill="currentColor" />
            )}
          </button>
        ) : (
          <button
            type="submit"
            disabled={!canSend}
            className={clsx(
              'flex-shrink-0 w-10 h-10 rounded-full',
              'flex items-center justify-center',
              'bg-primary text-primary-foreground',
              'hover:bg-primary/90 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary'
            )}
          >
            {isUploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-0 mb-2 text-center">
        Press Enter to send, Shift+Enter for new line
      </p>
    </form>
  );
};

export default MessageInput;
